import { cn } from './cn';

export interface MultiSelectOption<V extends string = string> {
  value: V;
  label: string;
}

interface Props<V extends string = string> {
  label?: string;
  options: ReadonlyArray<MultiSelectOption<V>>;
  value: V[];
  onChange: (next: V[]) => void;
  testId?: string;
  className?: string;
  /** When set, render a "select all" toggle that flips to `clearLabel` once
   *  every option is selected. Omit both to hide the toggle. */
  selectAllLabel?: string;
  clearLabel?: string;
}

export function MultiSelectChips<V extends string = string>({
  label,
  options,
  value,
  onChange,
  testId,
  className,
  selectAllLabel,
  clearLabel,
}: Props<V>): JSX.Element {
  const toggle = (v: V): void => {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  };
  const allSelected = options.length > 0 && value.length >= options.length;
  const showToggle =
    options.length > 0 && Boolean(selectAllLabel ?? clearLabel);
  return (
    <div
      className={cn('flex flex-wrap items-center gap-2', className)}
      role="group"
      {...(label ? { 'aria-label': label } : {})}
      {...(testId ? { 'data-testid': testId } : {})}
    >
      {label ? (
        <span className="text-xs font-medium text-muted-foreground">
          {label}
        </span>
      ) : null}
      {showToggle ? (
        <button
          type="button"
          onClick={() =>
            onChange(allSelected ? [] : options.map((o) => o.value))
          }
          {...(testId ? { 'data-testid': `${testId}-toggle-all` } : {})}
          className={cn(
            'rounded-full border border-dashed border-input px-3 py-1',
            'text-xs font-medium text-muted-foreground transition-colors',
            'hover:border-foreground/30 hover:text-foreground',
            'focus-visible:outline-none focus-visible:ring-2',
            'focus-visible:ring-ring focus-visible:ring-offset-1',
          )}
        >
          {allSelected ? (clearLabel ?? selectAllLabel) : selectAllLabel}
        </button>
      ) : null}
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => {
          const active = value.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              role="checkbox"
              aria-checked={active}
              onClick={() => toggle(opt.value)}
              {...(testId
                ? { 'data-testid': `${testId}-${opt.value}` }
                : {})}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                active
                  ? 'border-primary bg-primary/10 text-primary shadow-sm'
                  : 'border-input bg-background text-muted-foreground hover:border-foreground/30 hover:text-foreground',
              )}
            >
              {active ? <CheckGlyph /> : null}
              <span>{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CheckGlyph(): JSX.Element {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
